import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa'

const Footer = () => {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()

  const footerLinks = {
    shop: [
      { name: t('shop.tabs.accessories'), href: '/shop#accessories' },
    ],
    company: [
      { name: t('nav.about'), href: '/about' },
      { name: t('nav.contact'), href: '/contact' },
      { name: 'AGB', href: '/terms' },
      { name: 'Impressum', href: '/impressum' },
      { name: 'Datenschutz', href: '/privacy' },
    ],
  }

  return (
    <footer className="relative bg-dark-900/80 backdrop-blur-xl border-t border-dark-700/50">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      
      <div className="container-custom px-4 sm:px-6 lg:px-8 py-12 sm:py-16 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-white text-xl sm:text-2xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-primary-400 to-purple-400 bg-clip-text text-transparent">
              24KennzeichenPro GmbH
            </h3>
            <p className="mb-6 text-gray-400 leading-relaxed">
              {t('home.subtitle')}
            </p>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 space-x-reverse text-gray-400 hover:text-primary-400 transition-colors">
                <FaPhone className="w-5 h-5" />
                <span>+49 (0) XXX XXX XXX</span>
              </div>
              <div className="flex items-center space-x-3 space-x-reverse text-gray-400 hover:text-primary-400 transition-colors">
                <FaEnvelope className="w-5 h-5" />
                <span>info@kennzeichenpro24.de</span>
              </div>
              <div className="flex items-center space-x-3 space-x-reverse text-gray-400">
                <FaMapMarkerAlt className="w-5 h-5" />
                <span>Deutschland</span>
              </div>
            </div>
          </motion.div>

          {/* Shop Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="text-white text-lg sm:text-xl font-semibold mb-4 sm:mb-6">{t('footer.shop')}</h4>
            <ul className="space-y-3">
              {footerLinks.shop.map((link, index) => (
                <motion.li
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 + index * 0.05 }}
                  whileHover={{ x: 5 }}
                >
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-primary-400 transition-colors flex items-center group"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-primary-400 transition-all duration-300 mr-0 group-hover:mr-2"></span>
                    {link.name}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Company Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="text-white text-xl font-semibold mb-6">{t('footer.company')}</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <motion.li
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.2 + index * 0.05 }}
                  whileHover={{ x: 5 }}
                >
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-primary-400 transition-colors flex items-center group"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-primary-400 transition-all duration-300 mr-0 group-hover:mr-2"></span>
                    {link.name}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Customer Service */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h4 className="text-white text-xl font-semibold mb-6">
              {t('footer.customerService')}
            </h4>
            <ul className="space-y-3">
              {[
                { name: t('nav.account'), href: '/account' },
                { name: t('nav.orders'), href: '/orders' },
                { name: t('nav.contact'), href: '/contact' },
              ].map((link, index) => (
                <motion.li
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.05 }}
                  whileHover={{ x: 5 }}
                >
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-primary-400 transition-colors flex items-center group"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-primary-400 transition-all duration-300 mr-0 group-hover:mr-2"></span>
                    {link.name}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>

        <div className="border-t border-dark-700/50 mt-8 sm:mt-12 pt-6 sm:pt-8 text-center">
          <p className="text-gray-500">
            &copy; {currentYear} 24KennzeichenPro GmbH. {t('footer.rights')}.
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
